import { getUserInfoStorage } from 'helpers/lib';
import { useCallback, useEffect, useRef, useState } from 'react';
import { NotificationService } from 'services/notification/notification.service';
import {
  NotificationSubscriptionRequest,
  TestPushNotificationRequest,
} from 'services/notification/notification.type';

export interface NotificationState {
  isSupported: boolean;
  permission: NotificationPermission;
  isSubscribed: boolean;
  subscription: PushSubscription | null;
  subscriptionId: string | null;
  error: string | null;
  loading: boolean;
}

export type NotificationPermission = 'default' | 'granted' | 'denied';

export const useNotification = () => {
  const [state, setState] = useState<NotificationState>({
    isSupported:
      'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window,
    permission: 'default',
    isSubscribed: false,
    subscription: null,
    subscriptionId: null,
    error: null,
    loading: false,
  });

  // Cache for VAPID key to prevent multiple API calls
  const vapidKeyCache = useRef<string | null>(null);

  // Prevent multiple simultaneous subscription attempts
  const isSubscribing = useRef<boolean>(false);

  // Check if notifications are supported
  const isSupported =
    'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;

  // Detect if browser is Microsoft Edge
  const _isEdge = useCallback(() => {
    return /Edg/.test(navigator.userAgent);
  }, []);

  // Detect if browser is Safari
  const _isSafari = useCallback(() => {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  }, []);

  // Check if Edge is using quiet notification requests
  const _isEdgeUsingQuietRequests = useCallback(() => {
    if (!_isEdge()) return false;

    // Edge uses quiet requests when:
    // 1. Site has low acceptance rate in Edge's scoring system
    // 2. User has blocked notifications 3+ times on different sites
    // 3. User has manually enabled quiet requests in settings

    // We can't directly detect this, but we can make an educated guess
    // based on the fact that requestPermission() doesn't show a prompt
    return true; // Assume Edge is using quiet requests for this site
  }, [_isEdge]);

  // Request notification permission
  const requestPermission = useCallback(async (): Promise<boolean> => {
    console.log('🔔 requestPermission called', {
      isSupported,
      currentPermission: Notification.permission,
      isSafari: _isSafari(),
    });
    if (!isSupported) {
      setState((prev) => ({ ...prev, error: 'Notifications are not supported in this browser' }));
      return false;
    }

    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      // Check if permission is already granted
      if (Notification.permission === 'granted') {
        console.log('🔔 Permission already granted');
        setState((prev) => ({ ...prev, permission: 'granted', loading: false }));
        return true;
      }

      // Check if permission is denied
      if (Notification.permission === 'denied') {
        console.log('🔔 Permission already denied');
        const isEdge = _isEdge();
        const isSafari = _isSafari();
        let errorMessage = 'Notification permission denied by user';

        if (isEdge) {
          errorMessage =
            'Edge has blocked notifications for this site. Please enable them manually via the notification icon in the address bar.';
        } else if (isSafari) {
          errorMessage =
            'Safari has blocked notifications for this site. Please enable them manually in Safari > Settings > Websites > Notifications.';
        }

        setState((prev) => ({
          ...prev,
          permission: 'denied',
          loading: false,
          error: errorMessage,
        }));
        return false;
      }

      // For Safari, we need to check if this is called from a user gesture
      const isSafari = _isSafari();
      if (isSafari) {
        console.log('🔍 Safari detected - checking for user gesture requirement');

        // Safari requires user gesture for notification permission requests
        // If this is called automatically (not from user interaction), we should not proceed
        // and instead show a message to the user to click a button
        const hasUserGesture = (() => {
          // Check if we're in a user gesture context
          // This is a best-effort detection since we can't reliably detect user gestures
          try {
            // If we can access the event loop and it's not in a timeout/callback, it might be a user gesture
            return true; // We'll assume it's a user gesture if the function is called
          } catch (error) {
            return false;
          }
        })();

        if (!hasUserGesture) {
          console.log('🔔 Safari requires user gesture for notification permission');
          setState((prev) => ({
            ...prev,
            permission: 'default',
            loading: false,
            error:
              'Safari requires user interaction to request notification permission. Please click the notification button to enable notifications.',
          }));
          return false;
        }
      }

      // For Edge, try requestPermission() but with better handling
      const isEdge = /Edg/.test(navigator.userAgent);
      if (isEdge) {
        console.log('🔍 Edge detected - attempting requestPermission() with enhanced handling');

        // Try to request permission on Edge
        try {
          const permission = await Notification.requestPermission();
          console.log('🔔 Edge permission request result:', permission);

          setState((prev) => ({
            ...prev,
            permission: permission as NotificationPermission,
            loading: false,
          }));

          if (permission === 'granted') {
            console.log('🔔 Edge permission granted successfully');
            return true;
          } else {
            console.log('🔔 Edge permission not granted:', permission);
            setState((prev) => ({
              ...prev,
              error:
                'Notification permission denied on Edge. Please check the notification icon in the address bar.',
            }));
            return false;
          }
        } catch (error) {
          console.error('❌ Edge permission request failed:', error);
          setState((prev) => ({
            ...prev,
            permission: 'default',
            loading: false,
            error:
              'Edge blocked the notification request. Please enable notifications manually via the notification icon in the address bar.',
          }));
          return false;
        }
      }

      // Request permission for other browsers
      console.log('🔔 Requesting notification permission...');

      // Add timeout to handle cases where browser blocks the request
      const permissionPromise = Notification.requestPermission();
      const timeoutPromise = new Promise<string>((_, reject) => {
        setTimeout(
          () =>
            reject(new Error('Permission request timeout - Browser may have blocked the request')),
          5000
        );
      });

      const permission = await Promise.race([permissionPromise, timeoutPromise]);
      console.log('🔔 Permission request result:', permission);

      setState((prev) => ({
        ...prev,
        permission: permission as NotificationPermission,
        loading: false,
      }));

      if (permission === 'granted') {
        console.log('🔔 Permission granted successfully');
        return true;
      } else {
        console.log('🔔 Permission not granted:', permission);
        setState((prev) => ({
          ...prev,
          error: 'Notification permission denied',
        }));
        return false;
      }
    } catch (error) {
      console.error('❌ Error requesting notification permission:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to request notification permission';
      setState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
      return false;
    }
  }, [isSupported, _isEdge, _isSafari]);

  // Get VAPID key from API with caching
  const getVapidKey = useCallback(async (): Promise<string> => {
    // Return cached key if available
    if (vapidKeyCache.current) {
      console.log('Using cached VAPID key:', vapidKeyCache.current);
      return vapidKeyCache.current;
    }

    try {
      console.log('Fetching VAPID key from API...');
      const response = await NotificationService.getVapidPublicKey();
      console.log('VAPID key API response:', response);

      // API returns the key directly as a string
      if (response && typeof response === 'string' && response.length > 0) {
        console.log('Using VAPID key from API:', response);
        vapidKeyCache.current = response; // Cache the key
        return response;
      } else {
        throw new Error('Invalid VAPID key from API response');
      }
    } catch (error) {
      console.warn('Failed to get VAPID key from API, using fallback:', error);

      // Fallback to environment variable or default
      const envKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      const defaultKey = 'BEl62iUYgUivxIkv69yViEuiBIa40HI0QYfF4QJ8K4Q';
      const fallbackKey = envKey || defaultKey;

      console.log('Using fallback VAPID key:', fallbackKey);
      vapidKeyCache.current = fallbackKey; // Cache the fallback key too
      return fallbackKey;
    }
  }, []);

  // Convert VAPID key from base64url to Uint8Array
  const urlBase64ToUint8Array = useCallback((base64String: string): Uint8Array => {
    try {
      if (!base64String || typeof base64String !== 'string') {
        throw new Error('Invalid base64 string provided');
      }

      const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
      const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

      console.log('Base64 string length:', base64String.length);
      console.log('Padded base64 length:', base64.length);

      const rawData = window.atob(base64);
      const outputArray = new Uint8Array(rawData.length);

      for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
      }

      console.log('Converted VAPID key to Uint8Array, length:', outputArray.length);
      return outputArray;
    } catch (error) {
      console.error('Error converting VAPID key:', error);
      throw new Error(
        `Failed to convert VAPID key: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }, []);

  // Subscribe to push notifications
  const subscribeToNotifications = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      setState((prev) => ({ ...prev, error: 'Notifications are not supported in this browser' }));
      return false;
    }

    // Prevent multiple simultaneous subscription attempts
    if (isSubscribing.current) {
      console.log('🔔 Subscription already in progress, skipping...');
      return false;
    }

    try {
      isSubscribing.current = true;
      setState((prev) => ({ ...prev, loading: true, error: null }));

      // Request permission first
      const hasPermission = await requestPermission();
      console.log('hasPermission', hasPermission);
      if (!hasPermission) {
        return false;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.getRegistration();
      console.log('registration', registration);
      if (!registration) {
        throw new Error('Service worker not registered');
      }

      // Check if there's already an active subscription
      const existingSubscription = await registration.pushManager.getSubscription();
      console.log('existingSubscription', existingSubscription);

      if (existingSubscription) {
        console.log('Found existing subscription, checking if it matches current VAPID key...');

        // Check if the existing subscription is still valid
        try {
          const subscriptionData = existingSubscription.toJSON();
          console.log('Existing subscription data:', subscriptionData);

          // If subscription exists and has valid keys, use it
          if (subscriptionData.keys && subscriptionData.keys.auth && subscriptionData.keys.p256dh) {
            console.log('Using existing subscription');
            setState((prev) => ({
              ...prev,
              isSubscribed: true,
              subscription: existingSubscription,
              loading: false,
              error: null,
            }));
            return true;
          }
        } catch (error) {
          console.warn('Error checking existing subscription:', error);
        }

        // If existing subscription is invalid, unsubscribe first
        console.log('Existing subscription is invalid, unsubscribing...');
        await existingSubscription.unsubscribe();
      }

      // Get VAPID key from API
      const vapidKey = await getVapidKey();
      console.log('VAPID Key:', vapidKey);

      if (!vapidKey || vapidKey.length === 0) {
        throw new Error(
          'VAPID key is not configured. Please check API endpoint or set REACT_APP_VAPID_PUBLIC_KEY in your environment variables.'
        );
      }

      const _convertedKey = urlBase64ToUint8Array(vapidKey);
      console.log('Converted VAPID Key length:', _convertedKey.length);

      // Create new subscription
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: _convertedKey,
      });
      console.log('Push subscription created successfully:', subscription);
      // Get subscription data
      const subscriptionData = subscription.toJSON();
      if (!subscriptionData.keys) {
        throw new Error('Failed to get subscription keys');
      }
      console.log('subscriptionData', subscriptionData);
      // Get current user info (for future use if needed)
      const _userInfo = getUserInfoStorage();

      // Prepare subscription request
      const subscriptionRequest: NotificationSubscriptionRequest = {
        id: crypto.randomUUID(),
        agent: navigator.userAgent,
        endpoint: subscriptionData.endpoint || '',
        auth: subscriptionData.keys.auth || '',
        p256dh: subscriptionData.keys.p256dh || '',
      };
      console.log('subscriptionRequest', subscriptionRequest);
      // Call API to register subscription
      try {
        console.log('Calling subscribe API...');
        const response = await NotificationService.subscribeNotification(subscriptionRequest);
        console.log('Subscribe API response:', response);
        console.log('Response keys:', Object.keys(response));
        console.log('Response.success:', response.success);
        console.log('Response.id:', response.id);
        console.log('Response.subscriptionId:', response.subscriptionId);

        // Check if response has id field (API success) or success field
        if (response.id || response.success === true) {
          console.log('Subscription API call successful!');
          const subscriptionId = response.id || response.subscriptionId || null;
          console.log('Final subscription ID to save:', subscriptionId);

          // Save subscription ID to localStorage
          if (subscriptionId) {
            localStorage.setItem('notification_subscription_id', subscriptionId);
            console.log('✅ Saved subscription ID to localStorage:', subscriptionId);
          } else {
            console.warn('⚠️ No subscription ID found in API response');
          }

          setState((prev) => ({
            ...prev,
            isSubscribed: true,
            subscription,
            subscriptionId,
            loading: false,
            error: null,
          }));
          return true;
        } else {
          console.warn(
            'API subscription failed, but continuing with local subscription:',
            response.message || 'Unknown error'
          );
        }
      } catch (apiError) {
        console.error('API subscription failed with error:', apiError);
        console.warn('Continuing with local subscription despite API failure');
      }

      // Set as subscribed even if API fails (for testing purposes)
      // Generate a temporary subscription ID for local use
      const tempSubscriptionId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      console.log('Using temporary subscription ID:', tempSubscriptionId);

      // Save temporary subscription ID to localStorage
      localStorage.setItem('notification_subscription_id', tempSubscriptionId);
      console.log('✅ Saved temporary subscription ID to localStorage:', tempSubscriptionId);

      setState((prev) => ({
        ...prev,
        isSubscribed: true,
        subscription,
        subscriptionId: tempSubscriptionId,
        loading: false,
        error: null,
      }));
      return true;
    } catch (error) {
      console.error('Push subscription error:', error);
      let errorMessage = 'Failed to subscribe to notifications';

      if (error instanceof Error) {
        errorMessage = error.message;

        // Provide more specific error messages
        if (error.message.includes('VAPID')) {
          errorMessage = 'VAPID key configuration error. Please check your environment variables.';
        } else if (error.message.includes('permission')) {
          errorMessage =
            'Notification permission denied. Please allow notifications in your browser settings.';
        } else if (error.message.includes('Service worker')) {
          errorMessage = 'Service worker not available. Please refresh the page and try again.';
        }
      }

      setState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
      return false;
    } finally {
      // Always reset the subscribing flag
      isSubscribing.current = false;
    }
  }, [isSupported, requestPermission, getVapidKey, urlBase64ToUint8Array]);

  // Unsubscribe from notifications
  const unsubscribeFromNotifications = useCallback(async (): Promise<boolean> => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      // Get subscription ID from state or localStorage
      const subscriptionId =
        state.subscriptionId || localStorage.getItem('notification_subscription_id');
      console.log('🔍 Unsubscribing with subscription ID:', subscriptionId);

      // Call API to unsubscribe if we have subscriptionId
      if (subscriptionId) {
        try {
          await NotificationService.unsubscribeNotification(subscriptionId);
          console.log('✅ Successfully called unsubscribe API');
        } catch (apiError) {
          console.warn('API unsubscribe failed, but continuing with local unsubscribe:', apiError);
        }
      } else {
        console.log('⚠️ No subscription ID found, skipping API unsubscribe');
      }

      // Unsubscribe locally
      if (state.subscription) {
        await state.subscription.unsubscribe();
      }

      // Clear subscription ID from localStorage
      localStorage.removeItem('notification_subscription_id');

      setState((prev) => ({
        ...prev,
        isSubscribed: false,
        subscription: null,
        subscriptionId: null,
        loading: false,
        error: null,
      }));

      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to unsubscribe from notifications';
      setState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
      return false;
    }
  }, [state.subscription, state.subscriptionId]);

  // Unregister service worker
  const unregisterServiceWorker = useCallback(async (): Promise<boolean> => {
    try {
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          console.log('🔍 Unregistering service worker...');
          await registration.unregister();
          console.log('✅ Service worker unregistered successfully');
        }
        return true;
      }
      return true; // No service worker support
    } catch (error) {
      console.warn('⚠️ Error unregistering service worker:', error);
      return false;
    }
  }, []);

  // Test push notification
  const testPushNotification = useCallback(async (): Promise<boolean> => {
    if (!state.subscription) {
      setState((prev) => ({
        ...prev,
        error: 'No active subscription found. Please subscribe first.',
      }));
      return false;
    }

    // Check notification permission
    if (state.permission !== 'granted') {
      setState((prev) => ({
        ...prev,
        error: 'Notification permission not granted. Please allow notifications first.',
      }));
      return false;
    }

    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      // Get current user info
      const userInfo = getUserInfoStorage();
      const userEmail = userInfo?.email || userInfo?.username || 'test@example.com';

      // Prepare test push data
      const testData: TestPushNotificationRequest = {
        email: userEmail,
        endpoint: state.subscription.endpoint,
        category: 'test',
        title: 'Test Push Notification',
        body: 'This is a test push notification from your app!',
        data: {
          timestamp: Date.now(),
          source: 'web-app',
          test: true,
        },
      };

      // Call API to test push notification

      try {
        const response = await NotificationService.testPushNotification(testData);
        // Check if response indicates success
        const responseAny = response as any;
        const isSuccess =
          response &&
          (responseAny.success === true ||
            (responseAny.message && responseAny.message.includes('Pushed the notification')) ||
            (typeof response === 'string' &&
              (response as string).includes('Pushed the notification')));

        if (isSuccess) {
          setState((prev) => ({ ...prev, loading: false, error: null }));
          return true;
        } else {
          setState((prev) => ({
            ...prev,
            loading: false,
            error: responseAny?.message || 'API call failed',
          }));
        }
      } catch (apiError: any) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: `API Error: ${apiError?.message || 'Unknown error'}`,
        }));
      }

      setState((prev) => ({ ...prev, loading: false, error: null }));
      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to send test push notification';
      setState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
      return false;
    }
  }, [state.subscription, state.permission]);

  // Clear error
  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  // Unsubscribe on logout (only call API, don't change local state)
  const unsubscribeOnLogout = useCallback(async (): Promise<boolean> => {
    try {
      if (state.subscriptionId) {
        await NotificationService.unsubscribeNotification(state.subscriptionId);
        console.log('Successfully unsubscribed from notifications on logout');
        return true;
      }
      return true; // No subscription to unsubscribe
    } catch (error) {
      console.warn('Failed to unsubscribe on logout:', error);
      return false;
    }
  }, [state.subscriptionId]);

  // Check current permission status and existing subscription
  useEffect(() => {
    if (isSupported) {
      setState((prev) => ({ ...prev, permission: Notification.permission }));

      // Check for existing subscription
      const checkExistingSubscription = async () => {
        try {
          // Always try to load subscription ID from localStorage first
          const savedSubscriptionId = localStorage.getItem('notification_subscription_id');
          console.log('🔍 Saved subscription ID from localStorage:', savedSubscriptionId);

          const registration = await navigator.serviceWorker.getRegistration();
          if (registration) {
            const subscription = await registration.pushManager.getSubscription();
            if (subscription) {
              console.log('Found existing subscription on app load:', subscription);
              setState((prev) => ({
                ...prev,
                isSubscribed: true,
                subscription,
                subscriptionId: savedSubscriptionId,
              }));
            } else if (savedSubscriptionId) {
              // We have subscription ID but no active subscription
              console.log('⚠️ Found subscription ID but no active subscription, clearing...');
              localStorage.removeItem('notification_subscription_id');
            }
          } else if (savedSubscriptionId) {
            // We have subscription ID but no service worker
            console.log('⚠️ Found subscription ID but no service worker, clearing...');
            localStorage.removeItem('notification_subscription_id');
          }
        } catch (error) {
          console.warn('Error checking existing subscription on load:', error);
        }
      };

      checkExistingSubscription();
    }
  }, [isSupported]);

  return {
    state,
    isSupported,
    requestPermission,
    subscribeToNotifications,
    unsubscribeFromNotifications,
    unregisterServiceWorker,
    unsubscribeOnLogout,
    testPushNotification,
    clearError,
  };
};
